import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Sparkles, X, Send, Camera, ImagePlus, Paperclip, Mic } from 'lucide-react';
import { getServiceConfig, AiChip, ServiceId } from '../lib/serviceData';
type MessageRole = 'ai' | 'user';
interface Attachment {
  name: string;
  url: string;
}
interface Message {
  id: number;
  role: MessageRole;
  text?: string;
  chips?: AiChip[];
  attachments?: Attachment[];
  showUpload?: boolean;
}
export function AiAssistantWidget() {
  const location = useLocation();
  // Pull the active service from route state (preferred) or sessionStorage fallback.
  const serviceId = location.state?.service as ServiceId | undefined || (typeof window !== 'undefined' ? sessionStorage.getItem('pair2.activeService') as ServiceId | null : null) || 'home';
  const service = getServiceConfig(serviceId);
  const buildIntro = (): Message[] => [{
    id: Date.now(),
    role: 'ai',
    text: service.aiIntro.text,
    chips: service.aiIntro.chips,
    showUpload: service.aiIntro.showUpload
  }];
  const [open, setOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [messages, setMessages] = useState<Message[]>(buildIntro);
  const [input, setInput] = useState('');
  const [uploadedCount, setUploadedCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  // When the active service changes, reset the conversation to match.
  useEffect(() => {
    setMessages(buildIntro());
    setUploadedCount(0);
    setHasUnread(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);
  const openPanel = () => {
    setOpen(true);
    setHasUnread(false);
  };
  const handleChip = (chip: AiChip) => {
    const userMsg: Message = {
      id: Date.now(),
      role: 'user',
      text: chip.label
    };
    let aiReply: Message;
    switch (chip.next) {
      case 'upload':
      case 'photos':
        aiReply = {
          id: Date.now() + 1,
          role: 'ai',
          text: "Drop your files below — I'll analyze and attach to the supplier brief.",
          showUpload: true
        };
        break;
      case 'skip':
        aiReply = {
          id: Date.now() + 1,
          role: 'ai',
          text: service.aiFollowUps.skipReply.text,
          chips: service.aiFollowUps.skipReply.chips
        };
        break;
      case 'fragile':
        aiReply = {
          id: Date.now() + 1,
          role: 'ai',
          text: 'Flagged as priority. The carrier will be briefed before pickup.'
        };
        break;
      case 'help':
        aiReply = {
          id: Date.now() + 1,
          role: 'ai',
          text: "Got it — I've added that to the carrier brief and locked the recommendation."
        };
        break;
      case 'done':
      default:
        aiReply = {
          id: Date.now() + 1,
          role: 'ai',
          text: service.aiFollowUps.defaultReply
        };
    }
    setMessages(m => [...m, userMsg, aiReply]);
  };
  const handleUpload = () => {
    const next = uploadedCount + 1;
    setUploadedCount(next);
    const userMsg: Message = {
      id: Date.now(),
      role: 'user',
      attachments: [{
        name: `${service.shortName.toLowerCase()}_${next}.jpg`,
        url: `https://images.unsplash.com/photo-${next % 2 === 0 ? '1555041469-a586c61ea9bc' : '1583847268964-b28dc8f51f92'}?auto=format&fit=crop&w=200&h=150&q=70`
      }]
    };
    const aiText = next === 1 ? service.aiFollowUps.uploadFirst : next === 2 ? service.aiFollowUps.uploadSecond : service.aiFollowUps.uploadThird;
    const aiMsg: Message = {
      id: Date.now() + 1,
      role: 'ai',
      text: aiText,
      chips: next >= 2 ? [{
        label: 'Send to suppliers',
        next: 'done'
      }, {
        label: 'Add one more',
        next: 'upload'
      }] : [{
        label: 'Add another',
        next: 'upload'
      }, {
        label: "That's all",
        next: 'done'
      }]
    };
    setMessages(m => [...m, userMsg, aiMsg]);
  };
  const handleSend = () => {
    if (!input.trim()) return;
    setMessages(m => [...m, {
      id: Date.now(),
      role: 'user',
      text: input
    }, {
      id: Date.now() + 1,
      role: 'ai',
      text: service.aiFollowUps.defaultReply
    }]);
    setInput('');
  };
  return <>
      {/* Collapsed bubble */}
      <AnimatePresence data-id="element-60">
        {!open && <motion.button key="bubble" initial={{
        opacity: 0,
        scale: 0.6,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.6,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 22,
        stiffness: 320
      }} onClick={openPanel} className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-white border border-gray-200 shadow-[0_8px_28px_-8px_rgba(20,184,166,0.45)] hover:shadow-[0_12px_36px_-8px_rgba(20,184,166,0.6)] hover:border-brand-300 transition-all" data-id="element-61">
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full orange-gradient-bg text-white shrink-0" data-id="element-62">
              <Sparkles className="w-3.5 h-3.5" data-id="element-63" />
              {hasUnread && <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3" data-id="element-64">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75" data-id="element-65" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-500 border-2 border-white" data-id="element-66" />
                </span>}
            </span>
            <span className="text-xs font-semibold text-gray-800 pr-1" data-id="element-67">
              Ask Pair2 AI
            </span>
          </motion.button>}
      </AnimatePresence>

      {/* Open chat panel */}
      <AnimatePresence data-id="element-68">
        {open && <motion.div key="panel" initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 320
      }} className="fixed bottom-5 right-5 z-50 w-[340px] max-w-[calc(100vw-2rem)]" data-id="element-69">
            <div className="bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.18)] border border-gray-100 overflow-hidden flex flex-col max-h-[520px]" data-id="element-70">
              {/* Slim header */}
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-71">
                <div className="flex items-center gap-2.5" data-id="element-72">
                  <div className="relative" data-id="element-73">
                    <div className="w-8 h-8 rounded-full orange-gradient-bg flex items-center justify-center shadow-sm" data-id="element-74">
                      <Sparkles className="w-4 h-4 text-white" data-id="element-75" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 border-2 border-white rounded-full" data-id="element-76" />
                  </div>
                  <div data-id="element-77">
                    <p className="font-semibold text-sm text-gray-900 leading-tight" data-id="element-78">
                      Pair2 AI
                    </p>
                    <p className="text-[10px] text-gray-500 font-medium leading-tight" data-id="element-79">
                      Online · {service.aiTopic}
                    </p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500" aria-label="Close" data-id="element-80">
                  <X className="w-4 h-4" data-id="element-81" />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" style={{
            maxHeight: '360px'
          }} data-id="element-82">
                {messages.map(msg => <MessageBubble key={msg.id} msg={msg} onChip={handleChip} onUpload={handleUpload} data-id="element-83" />)}
              </div>

              {/* Input */}
              <div className="border-t border-gray-100 p-2.5 bg-white" data-id="element-84">
                <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-2.5 py-1.5 border border-gray-100" data-id="element-85">
                  <button onClick={handleUpload} className="text-gray-400 hover:text-brand-500 transition-colors p-1" aria-label="Attach" data-id="element-86">
                    <Paperclip className="w-3.5 h-3.5" data-id="element-87" />
                  </button>
                  <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder="Reply..." className="flex-1 bg-transparent outline-none text-xs placeholder:text-gray-400 py-1" data-id="element-88" />
                  <button className="text-gray-400 hover:text-brand-500 transition-colors p-1" aria-label="Voice" data-id="element-89">
                    <Mic className="w-3.5 h-3.5" data-id="element-90" />
                  </button>
                  <button onClick={handleSend} disabled={!input.trim()} className="w-6 h-6 rounded-full orange-gradient-bg text-white flex items-center justify-center disabled:opacity-40 transition-opacity" aria-label="Send" data-id="element-91">
                    <Send className="w-3 h-3" data-id="element-92" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function MessageBubble({
  msg,
  onChip,
  onUpload
}: {
  msg: Message;
  onChip: (c: AiChip) => void;
  onUpload: () => void;
}) {
  if (msg.role === 'user') {
    return <motion.div initial={{
      opacity: 0,
      x: 10
    }} animate={{
      opacity: 1,
      x: 0
    }} className="flex justify-end" data-id="element-93">
        <div className="max-w-[82%]" data-id="element-94">
          {msg.text && <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-2xl rounded-br-md leading-relaxed" data-id="element-95">
              {msg.text}
            </div>}
          {msg.attachments && <div className="flex gap-1.5 mt-1 justify-end flex-wrap" data-id="element-96">
              {msg.attachments.map((att, i) => <div key={i} className="relative w-20 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm" data-id="element-97">
                  <img src={att.url} alt={att.name} className="w-full h-full object-cover" data-id="element-98" />
                </div>)}
            </div>}
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} className="flex gap-2" data-id="element-99">
      <div className="w-6 h-6 rounded-full orange-gradient-bg flex items-center justify-center shrink-0 mt-0.5" data-id="element-100">
        <Sparkles className="w-3 h-3 text-white" data-id="element-101" />
      </div>
      <div className="flex-1 max-w-[85%]" data-id="element-102">
        {msg.text && <div className="bg-gray-50 border border-gray-100 text-gray-800 text-xs px-3 py-2 rounded-2xl rounded-tl-md leading-relaxed" data-id="element-103">
            {msg.text}
          </div>}
        {msg.showUpload && <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-104">
            <button onClick={onUpload} className="flex items-center justify-center gap-1.5 py-2 border border-dashed border-brand-200 hover:border-brand-400 bg-brand-50/40 hover:bg-brand-50 rounded-xl transition-colors text-brand-700" data-id="element-105">
              <ImagePlus className="w-3.5 h-3.5" data-id="element-106" />
              <span className="text-[10px] font-semibold" data-id="element-107">Upload</span>
            </button>
            <button onClick={onUpload} className="flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-200 hover:border-gray-300 bg-gray-50/60 hover:bg-gray-100 rounded-xl transition-colors text-gray-700" data-id="element-108">
              <Camera className="w-3.5 h-3.5" data-id="element-109" />
              <span className="text-[10px] font-semibold" data-id="element-110">Camera</span>
            </button>
          </div>}
        {msg.chips && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-111">
            {msg.chips.map((chip, i) => <button key={i} onClick={() => onChip(chip)} className="text-[10px] font-medium px-2.5 py-1 bg-white border border-gray-200 hover:border-brand-400 hover:text-brand-700 hover:bg-brand-50 text-gray-700 rounded-full transition-colors" data-id="element-112">
                {chip.label}
              </button>)}
          </div>}
      </div>
    </motion.div>;
}